import React, { useState, useEffect, useRef } from 'react';
import varListData from './VarList.json';
import api from '../services/api';
import './norms.css';
import ExportButton from './ExportButton';

// ── Design tokens ────────────────────────────────────────────────────────────
const inp = (extra = {}) => ({
  width: '100%', padding: '8px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8,
  fontSize: 13, outline: 'none', color: '#334155', background: '#fff',
  fontFamily: 'inherit', boxSizing: 'border-box', ...extra,
});
const btnStyle = (bg = '#0f172a', color = '#fff', extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color,
  border: bg === 'transparent' ? '1.5px solid #e2e8f0' : 'none',
  borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', ...extra,
});
const labelCss = { fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

// ── Accordion panel config — avoids 13 copy-pasted blocks ────────────────────
const getFilterPanels = (s) => [
  { id: 'clientname',            label: 'Client',                   options: s.clientNameOptions },
  { id: 'studytype',             label: 'Study Type',               options: s.studyTypeOptions },
  { id: 'year',                  label: 'Year',                     options: s.yearOptions },
  { id: 'countrycode',           label: 'Country',                  options: s.countryCodeOptions },
  { id: 'supercategory',         label: 'Super Category',           options: s.superCategoryOptions },
  { id: 'category',             label: 'Category',                 options: s.categoryOptions },
  { id: 'subcategory',           label: 'Sub Category',             options: s.subCategoryOptions },
  { id: 'studydesign',           label: 'Study Design',             options: s.studyDesignOptions },
  { id: 'placeofinterview',      label: 'Place of Interview',       options: s.placeOfInterviewOptions },
  { id: 'blindbranded',          label: 'Blind / Branded',          options: s.blindOrBrandedOptions },
  { id: 'scaleoverallopinion',   label: 'Scale: Overall Opinion',   options: s.scaleOverallOpinionOptions },
  { id: 'scalepurchaseintention',label: 'Scale: Purchase Intention',options: s.scalePurchaseIntentionOptions },
  { id: 'scaleproductattributes',label: 'Scale: Product Attributes',options: s.scaleProductAttributesOptions },
];

// ── Header color for norms table ─────────────────────────────────────────────
const getHeaderColor = (index) => {
  if (index < 5) return '#C00000';
  if (index < 7) return '#FFC000';
  return '#92D050';
};

// ── Main component ───────────────────────────────────────────────────────────
const Norms = () => {
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState('');
  const [result, setResult] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meanValue, setMean] = useState();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [filterLogicData, setFilterLogicData] = useState(null);
  const [filterNames, setFilterNames] = useState([]);
  const [optionListValue, setOptionListValue] = useState([]);
  const [isValid, setValid] = useState(false);
  const [clientNameOptions, setClientNameOptions] = useState([]);
  const [studyTypeOptions, setStudyTypeOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [countryCodeOptions, setCountryCodeOptions] = useState([]);
  const [superCategoryOptions, setSuperCategoryOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [studyDesignOptions, setStudyDesignOptions] = useState([]);
  const [placeOfInterviewOptions, setPlaceOfInterviewOptions] = useState([]);
  const [blindOrBrandedOptions, setBlindOrBrandedOptions] = useState([]);
  const [scaleOverallOpinionOptions, setScaleOverallOpinionOptions] = useState([]);
  const [scalePurchaseIntentionOptions, setScalePurchaseIntentionOptions] = useState([]);
  const [scaleProductAttributesOptions, setScaleProductAttributesOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [openAccordion, setOpenAccordion] = useState(null);
  const [static_filters, setFilters] = useState(null);
  const dropdownsFetched = useRef(false);
  const [loadingFilters, setLoadingFilters] = useState({});
  const [selectedOptionsString, setSelectedOptionsString] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleAccordionToggle = async (accordionId) => {
    const isClosing = openAccordion === accordionId;
    setOpenAccordion(isClosing ? null : accordionId);
    if (isClosing) {
      setRefreshKey((k) => k + 1);
      return;
    }
    if (openAccordion !== accordionId) {
      setLoadingFilters((prev) => ({ ...prev, [accordionId]: true }));
      try {
        const response = await api.get('data/option-list/', {
          params: { question: accordionId, filter_name: filterLogicData, filters: selectedOptionsString },
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });
        const setters = {
          clientname: setClientNameOptions, studytype: setStudyTypeOptions,
          year: setYearOptions, countrycode: setCountryCodeOptions,
          supercategory: setSuperCategoryOptions, category: setCategoryOptions,
          subcategory: setSubCategoryOptions, studydesign: setStudyDesignOptions,
          placeofinterview: setPlaceOfInterviewOptions, blindbranded: setBlindOrBrandedOptions,
          scaleoverallopinion: setScaleOverallOpinionOptions,
          scalepurchaseintention: setScalePurchaseIntentionOptions,
          scaleproductattributes: setScaleProductAttributesOptions,
        };
        if (setters[accordionId]) setters[accordionId](response.data);
        else console.error('Invalid accordionId:', accordionId);
      } catch (err) {
        console.error(`Error fetching options for ${accordionId}:`, err);
      }
      setLoadingFilters((prev) => ({ ...prev, [accordionId]: false }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedOption) return;
      setLoading(true);
      setError(null);
      try {
        const str = Object.keys(selectedOptions).map((q) => {
          const opts = selectedOptions[q].join(', ');
          return opts ? `${q}: ${opts}` : '';
        }).filter(Boolean).join(' (AND) ');
        setFilters(str);
        const response = await api.get(`norms/percentiles/?normsVar=${selectedOption}`, {
          params: { filter_name: filterLogicData, filters: str },
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });
        setResult(response.data);
        setMean(Object.values(response.data)[3]);
        setValid(true);
      } catch {
        setError('Invalid attribute | Blank attribute');
        setValid(false);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedOption, filterLogicData, refreshKey]);

  useEffect(() => {
    const fetchFilterNames = async () => {
      setIsPageLoading(true);
      try {
        const response = await api.get('filter/filter-names/', {
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });
        setFilterNames(response.data);
      } catch (err) {
        console.error('Error fetching filter names:', err);
      }
      setIsPageLoading(false);
    };
    fetchFilterNames();
  }, []);

  useEffect(() => {
    const str = Object.keys(selectedOptions).map((q) => {
      const opts = selectedOptions[q].join(', ');
      return opts ? `${q}: ${opts}` : '';
    }).filter(Boolean).join(' (AND) ');
    setSelectedOptionsString(str);
  }, [selectedOptions]);

  const filteredVarListData = varListData.filter(
    (item) => item.label && item.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleSelectedFilter = (item) => setFilterLogicData(item.filter_name);
  const handleOptionToggle = (option, question) => {
    setSelectedOptions((prev) => {
      const cur = prev[question] || [];
      return { ...prev, [question]: cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option] };
    });
  };
  const handleReset = () => { setSelectedOptions({}); alert(" 'Click OK To Reset' "); };
  const getSelectedCount = (id) => {
    const n = selectedOptions[id]?.length;
    return n > 0 ? ` (${n})` : '';
  };
  const handleChange = (e) => { setSelectedOption(e.target.value); setFilterLogicData(null); };
  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handleFilterSelection = (filter) => {
    setSelectedFilter(filter.filter_name);
    setFilterLogicData(filter ? filter.filter_name : null);
  };
  const handleRemoveFilter = (filterNameToRemove) => {
    setSelectedFilter(null);
    setFilterLogicData(filterNames.filter((f) => f.name !== filterNameToRemove).map((f) => f.filterLogic).flat());
  };
  const handleFilterNameAdded = (newFilterName, newFilterData) => {
    const idx = filterNames.findIndex((f) => f.name === newFilterName);
    if (idx !== -1) {
      const updated = [...filterNames];
      updated[idx] = { ...updated[idx], filterLogic: [...updated[idx].filterLogic, ...newFilterData] };
      setFilterNames(updated);
    } else {
      setFilterNames((prev) => [...prev, { name: newFilterName, filterLogic: newFilterData }]);
    }
  };

  const hasAnyFilter = Object.values(selectedOptions).some((o) => o.length > 0);

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderTable = () => {
    if (loading) return (
      <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 13 }}>
        <div style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#0f172a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
        Fetching results, please wait…
      </div>
    );
    if (error) return (
      <div style={{ background: '#fff1f2', borderLeft: '4px solid #f43f5e', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#9f1239' }}>{error}</div>
    );
    if (!result) return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Select an attribute above to view norms data.</div>
    );

    const values = Object.values(result);
    if (!Array.isArray(values) || values.length < 8) return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No data available.</div>
    );

    const percentiles = Object.keys(result).slice(1);
    const headers = values[0];
    const mean_value = values[3] != null ? values[3].toFixed(2) : 'N/A';
    const ssize = values[5];
    const nop = values[6];
    const filteredPercentiles = percentiles.filter(
      (p) => !['Mean Value', 'Samples considered', 'Number of products', 'Number of projects'].includes(p)
    );

    return (
      <div style={{ marginTop: 16 }}>
        {isPageLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            <ExportButton />
            <div style={{ overflowX: 'auto', marginTop: 12 }}>
              <table id="normsTable" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, border: '1px solid #e2e8f0' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11.5, color: '#475569', border: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>Percentile</th>
                    {headers.map((header, i) => (
                      <th key={i} style={{ padding: '9px 10px', background: getHeaderColor(i), color: '#fff', fontSize: 11.5, fontWeight: 700, border: '1px solid #e2e8f0', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredPercentiles.map((percentile, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ padding: '7px 12px', border: '1px solid #e2e8f0', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>{percentile}</td>
                      {Object.values(result[percentile]).map((value, j) => (
                        <td key={j} style={{ padding: '7px 10px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#475569', fontVariantNumeric: 'tabular-nums' }}>{value}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  {[
                    { label: 'Overall Mean', value: mean_value },
                    { label: 'Samples Considered', value: ssize },
                    { label: 'Number of Products', value: nop },
                  ].map((row, i) => (
                    <tr key={i} style={{ background: '#f8fafc', borderTop: i === 0 ? '2px solid #e2e8f0' : 'none' }}>
                      <td style={{ padding: '7px 12px', border: '1px solid #e2e8f0', fontWeight: 700, color: '#0f172a' }}>{row.label}</td>
                      <td colSpan={headers.length} style={{ padding: '7px 10px', border: '1px solid #e2e8f0', color: '#334155' }}>{row.value}</td>
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  // Build panel list for rendering
  const filterPanels = getFilterPanels({
    clientNameOptions, studyTypeOptions, yearOptions, countryCodeOptions,
    superCategoryOptions, categoryOptions, subCategoryOptions, studyDesignOptions,
    placeOfInterviewOptions, blindOrBrandedOptions, scaleOverallOpinionOptions,
    scalePurchaseIntentionOptions, scaleProductAttributesOptions,
  });

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <>
      {/* Inline keyframe for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ padding: '24px 28px', boxSizing: 'border-box' }}>

        {/* Page header */}
        <div style={{ background: '#0f172a', borderRadius: 12, padding: '18px 24px', marginBottom: 24 }}>
          <div style={{ fontSize: 19, fontWeight: 700, color: '#fff' }}>Norms</div>
          <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 2 }}>Percentile benchmarks by attribute</div>
        </div>

        {/* Body: two-column flex */}
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

          {/* ── Left main area ── */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Search + Attribute select */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px', marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ minWidth: 180 }}>
                  <label style={labelCss}>Search attribute</label>
                  <input
                    type="text"
                    placeholder="Type to filter…"
                    value={searchText}
                    onChange={handleSearchChange}
                    style={inp()}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <label style={labelCss}>Choose attribute</label>
                  <select value={selectedOption} onChange={handleChange} style={inp()}>
                    <option hidden value="">{searchText || 'Select an option'}</option>
                    {filteredVarListData.map((item, i) => (
                      <option key={i} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Named filter bar */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '14px 20px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Saved Filter:</span>
              <select
                value={selectedFilter || ''}
                onChange={(e) => {
                  if (!e.target.value) { handleRemoveFilter(selectedFilter); return; }
                  const f = filterNames.find((fn) => fn.filter_name === e.target.value);
                  if (f) { handleFilterSelection(f); handleSelectedFilter(f); }
                }}
                disabled={filterNames.length === 0 || !selectedOption}
                style={inp({ width: 'auto', minWidth: 200 })}
              >
                <option value="">— Apply Filter —</option>
                {filterNames.map((f, i) => (
                  <option key={i} value={f.filter_name}>{f.filter_name}</option>
                ))}
              </select>
              {selectedFilter && (
                <button style={btnStyle('#f43f5e', '#fff', { padding: '6px 12px', fontSize: 12 })} onClick={() => handleRemoveFilter(selectedFilter)}>
                  ✕ Remove
                </button>
              )}
              {static_filters && (
                <span style={{ fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{static_filters}</span>
              )}
            </div>

            {/* Results table */}
            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '18px 20px' }}>
              {renderTable()}
            </div>
          </div>

          {/* ── Right sidebar: Static Filters ── */}
          <div style={{ width: 240, flexShrink: 0 }}>
            <div style={{ background: '#0f172a', borderRadius: 12, padding: '14px 14px 18px', position: 'sticky', top: 20 }}>

              {/* Sidebar header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: hasAnyFilter ? '#fff' : '#64748b' }}>Static Filters</span>
                <button
                  onClick={handleReset}
                  disabled={!hasAnyFilter}
                  title="Reset all filters"
                  style={{ background: hasAnyFilter ? '#f43f5e' : 'rgba(255,255,255,0.07)', border: 'none', borderRadius: 6, cursor: hasAnyFilter ? 'pointer' : 'not-allowed', color: hasAnyFilter ? '#fff' : '#64748b', padding: '5px 8px', fontSize: 12, fontFamily: 'inherit' }}
                >
                  ↺
                </button>
              </div>

              {/* 13 accordion panels */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {filterPanels.map(({ id, label, options }) => {
                  const isOpen = openAccordion === id;
                  const count = getSelectedCount(id);
                  return (
                    <div key={id} style={{ borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                      <button
                        onClick={() => handleAccordionToggle(id)}
                        disabled={!selectedOption}
                        style={{
                          width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '8px 12px', background: isOpen ? '#f8fafc' : '#fff',
                          border: 'none', cursor: selectedOption ? 'pointer' : 'not-allowed',
                          fontSize: 12.5, fontWeight: isOpen ? 700 : 500, color: selectedOption ? '#0f172a' : '#94a3b8',
                          textAlign: 'left', fontFamily: 'inherit',
                        }}
                      >
                        <span>{label}{count && <span style={{ color: '#3b82f6', fontWeight: 700 }}>{count}</span>}</span>
                        <span style={{ fontSize: 10, color: '#94a3b8', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', display: 'inline-block' }}>▼</span>
                      </button>

                      {isOpen && (
                        <div style={{ borderTop: '1px solid #f1f5f9', maxHeight: 160, overflowY: 'auto', padding: '8px 12px' }}>
                          {loadingFilters[id] ? (
                            <div style={{ textAlign: 'center', padding: '8px 0', color: '#94a3b8', fontSize: 12 }}>Loading…</div>
                          ) : options.length === 0 ? (
                            <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '6px 0' }}>No options</div>
                          ) : options.map((option, i) => (
                            <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!!(selectedOptions[id] && selectedOptions[id].includes(option))}
                                onChange={() => handleOptionToggle(option, id)}
                                style={{ accentColor: '#0f172a', flexShrink: 0 }}
                              />
                              <span style={{ fontSize: 12, color: '#334155', lineHeight: 1.4 }}>{option}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Norms;
